// Simple Slider Web Component

if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.slideLeft = this.querySelector('[data-slide-left]');
      this.slideRight = this.querySelector('[data-slide-right]');
      this.dots = this.querySelectorAll('[data-dot]');
      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.size = this.sliderList ? this.sliderList.children.length : 0;

      // Determine initial index (start from middle if data-middle is present)
      this.initialIndex =
        this.hasAttribute('data-middle') && this.size > 0
          ? Math.floor(this.size / 2)
          : 0;

      this.currentIndex = this.initialIndex;
      this.isPointerDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragged = false; // Track whether dragging occurred
      this.hasScrolled = false; // Ensure only one scroll per click
      this.isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
      this._hasInitialIntersection = false;

      if (this.isSafari) {
        this.sensitivity = 1.5; // Adjust sensitivity for Safari
      } else {
        this.sensitivity = 1;
      }

      this.setupEventListeners();
      this.setupObservers();
      this.initializeToFirstSlide();
    }

    // Ensure slider state and position are consistently reset to the initial slide
    initializeToFirstSlide() {
      if (!this.sliderList) return;

      // Scroll to the initial slide (first or middle depending on data-middle)
      const target = this.sliderList.children[this.initialIndex] || this.sliderList.children[0];
      this.sliderList.scrollLeft = target ? target.offsetLeft : 0;

      // Make sure internal state matches the initial slide
      this.currentIndex = this.initialIndex;
      this.visibilityNavigation();
      this.visibilityDots();

      // Run again after full page load to override any browser scroll restoration
      window.addEventListener('load', () => {
        const loadTarget = this.sliderList.children[this.initialIndex] || this.sliderList.children[0];
        this.sliderList.scrollLeft = loadTarget ? loadTarget.offsetLeft : 0;
        this.currentIndex = this.initialIndex;
        this.visibilityNavigation();
        this.visibilityDots();
      }, { once: true });
    }

    setupEventListeners() {
      this.slideLeft?.addEventListener('click', () => this.scrollSliderLeft());
      this.slideRight?.addEventListener('click', () => this.scrollSliderRight());

      this.dots?.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index));
      });

      if (window.innerWidth >= 1024) {
        // Pointer (Mouse & Touch) drag support
        this.sliderList.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.sliderList.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.sliderList.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.sliderList.addEventListener('pointerleave', this.onPointerUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });
      }
    }

    onPointerDown(event) {
      this.isPointerDown = true;
      this.startX = event.pageX;
      this.scrollLeftStart = this.sliderList.scrollLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.hasScrolled = false; // Allow scrolling again
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
      event.preventDefault(); // Prevent unintended selection
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.hasScrolled) return;
      const moveX = (event.pageX - this.startX) * this.sensitivity;

      if (Math.abs(moveX) > 2) { // Reduced sensitivity for quicker response
        this.dragged = true;
      }

      if (Math.abs(moveX) > 20) { // Set threshold for one-time scroll
        if (moveX > 0) {
          this.scrollSliderLeft();
        } else {
          this.scrollSliderRight();
        }
        this.hasScrolled = true; // Prevent further scrolling until next click
      }
    }

    onPointerUp() {
      this.isPointerDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollSliderLeft() {
      this.sliderList.scrollLeft -= this.slide.clientWidth;
    }

    scrollSliderRight() {
      this.sliderList.scrollLeft += this.slide.clientWidth;
    }

    goToSlide(index) {
      if (!this.sliderList?.children[index]) return;
      const target = this.sliderList.children[index];
      this.sliderList.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    setupObservers() {
      // Intersection Observer for slides
      // Use a lower threshold to work with tall slides that exceed viewport height
      const threshold = 0.3;
      const rootMargin = `0px 0px 0px 0px`;
      const observerOptions = {
        threshold,
        rootMargin,
      };

      const sliderScrollObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          const index = parseInt(entry.target.dataset.index, 10);

          // On initial load, we want to stick to the initial slide
          // (first or middle) and ignore any transient intersections of others.
          if (!this._hasInitialIntersection) {
            if (index !== this.initialIndex) {
              return;
            }
            this._hasInitialIntersection = true;
          }

          this.currentIndex = index;
          this.visibilityNavigation();
          this.visibilityDots();
          console.log("Current index:", this.currentIndex, 'Slides per view:', this.getSlidesPerView());
        });
      }, observerOptions);

      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }


    getSlidesPerView() {
      const sliderContainer = this.sliderList;
      const slides = [...this.sliderList.children];

      if (!sliderContainer || slides.length === 0) return 1;

      const containerWidth = sliderContainer.clientWidth;
      const slideWidth = slides[0].clientWidth;
      const firstSlideOffset = slides[0].offsetLeft; // Distance from the left edge of the container

      // Read the actual gap from the list styles so JS stays in sync
      const computedStyle = window.getComputedStyle(sliderContainer);
      const rawGap = computedStyle.gap || '0';
      const gap = parseFloat(rawGap) || 0;

      // Subtract the gap for all but the last slide
      const adjustedWidth = containerWidth - firstSlideOffset;

      // Calculate the number of slides per view, including the gap
      const slidesPerView = Math.max(
        1,
        Math.floor((adjustedWidth + gap) / (slideWidth + gap)),
      );

      return slidesPerView;
    }

    visibilityNavigation() {
      if (this.currentIndex === 0) {
        this.slideLeft?.setAttribute('data-active', 'false');
      } else {
        this.slideLeft?.setAttribute('data-active', 'true');
      }

      if (this.currentIndex >= (this.size - this.getSlidesPerView())) {
        this.slideRight?.setAttribute('data-active', 'false');
      } else {
        this.slideRight?.setAttribute('data-active', 'true');
      }
    }

    visibilityDots() {
      this.dots.forEach((dot) => dot?.removeAttribute('data-active'));
      this.dots[this.currentIndex]?.setAttribute('data-active', '');
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}
